<template>
  <div v-if="isLoading || hasData">
    <PotentialFinancialImpactsSkeleton v-if="isLoading" />

    <div v-else-if="hasData">
      <div class="d-flex align-center">
        <span
          data-testid="home-text-predictedFinancialImpactsLabel"
          class="caption blue-grey--text text--darken-1 font-weight-medium"
        >
          {{ $t(titleKey) }}
        </span>

        <div class="ml-2 d-flex align-center">
          <PredictedFinancialImpactInfoTooltip
            data-testid="home-icon-predictedFinancialImpactsInfo"
            :text="$t('home.potential_financial_impacts.tooltip')"
          />
        </div>
      </div>

      <v-card
        class="d-flex align-center justify-space-between px-3 py-4 mt-3"
        data-testid="home-card-predictedFinancialImpacts"
        outlined
      >
        <div class="d-flex flex-column min-width-0 w-100">
          <div class="d-flex justify-start align-center min-width-0">
            <v-avatar size="28" color="green lighten-2 mr-2">
              <v-icon size="16" color="green" small> mdi-cash-multiple </v-icon>
            </v-avatar>

            <div class="text-truncate" :title="formattedTotal">
              <span
                class="h6 font-weight-500 green--text text--darken-2"
                data-testid="home-text-predictedFinancialImpactsTotal"
              >
                {{ formattedTotal }}
              </span>
            </div>
          </div>

          <p
            class="caption blue-grey--text text--darken-1 mt-2 mb-0"
            data-testid="home-text-predictedFinancialImpactsDescription"
          >
            {{ $t('rev.financial_potential_impact.description') }}
          </p>

          <Button
            block
            class="mt-4"
            color="primaryLighten"
            data-testid="home-button-viewAllPredictedFinancialImpacts"
            size="small"
            :text="$t('idea_match.view_revs')"
            :to="exploreRoute"
            @click="
              trackHomeNavigate({
                section: 'predictedFinancialImpacts',
                element: 'viewAll',
              })
            "
          />
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import Button from '@/components/UI/Button.vue'
import PredictedFinancialImpactInfoTooltip from '@/components/PredictedFinancialImpactInfoTooltip.vue'
import constants from '@/constants'
import mixin from '@/mixins/mixin.vue'
import moneyMixin from '@/mixins/moneyMixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import PotentialFinancialImpactsSkeleton from './PotentialFinancialImpactsSkeleton.vue'

import { HOME_WIDGET_FETCH_DELAYS } from '../constants'

const REPORT_TYPE = 'predicted-financial-impact-in-usd'

export default {
  name: 'PotentialFinancialImpacts',

  components: {
    Button,
    PredictedFinancialImpactInfoTooltip,
    PotentialFinancialImpactsSkeleton,
  },

  mixins: [trackingMixin, moneyMixin, mixin],

  data() {
    return {
      isLoading: true,
      isShowingCachedSnapshot: false,
      fetchTimer: null,
      predictedFinancialImpact: 0,
      constants,
    }
  },

  computed: {
    ...mapGetters(['accountId', 'account', 'organizationId', 'siteId']),

    isOrganizationAdmin() {
      return Boolean(this.account?.isAdmin || this.account?.isOrganizationAdmin)
    },

    titleKey() {
      return this.isOrganizationAdmin
        ? 'home.potential_financial_impacts.title_organization'
        : 'home.potential_financial_impacts.title_site'
    },

    statsQuery() {
      if (this.isOrganizationAdmin || !this.siteId) {
        return {}
      }

      return {
        filters: {
          sites: [this.siteId],
        },
      }
    },

    hasData() {
      return (
        _.isNumber(this.predictedFinancialImpact) &&
        this.predictedFinancialImpact >=
          constants.rev.minSignificantPredictedFinancialImpact
      )
    },

    formattedTotal() {
      return this.formatToUserCurrency(this.predictedFinancialImpact)
    },

    exploreRoute() {
      const query = {
        sort: 'predictedFinancialImpactInUSD-desc',
      }

      if (!this.isOrganizationAdmin && this.siteId) {
        query.sites = [this.siteId]
      }

      return {
        name: 'explore',
        query,
      }
    },
  },

  mounted() {
    this.hydrateFromSnapshot()
    this.fetchTimer = setTimeout(
      this.fetchPredictedFinancialImpact,
      HOME_WIDGET_FETCH_DELAYS.activityBoard
    )
  },

  beforeDestroy() {
    clearTimeout(this.fetchTimer)
  },

  methods: {
    hydrateFromSnapshot() {
      const snapshot = this.$store.getters['homeSnapshots/validSnapshot'](
        'activityBoard'
      )

      if (!_.isNumber(snapshot?.potentialFinancialImpact)) return

      const snapshotSiteId = snapshot.potentialFinancialImpactSiteId ?? null
      const currentSiteId = this.isOrganizationAdmin ? null : this.siteId || null

      if (snapshotSiteId !== currentSiteId) return

      this.predictedFinancialImpact = snapshot.potentialFinancialImpact
      this.isLoading = false
      this.isShowingCachedSnapshot = true
    },

    saveSnapshot() {
      this.$store.commit('homeSnapshots/SET_ACTIVITY_BOARD_SNAPSHOT', {
        organizationId: this.organizationId,
        accountId: this.accountId,
        potentialFinancialImpact: this.predictedFinancialImpact,
        potentialFinancialImpactSiteId: this.isOrganizationAdmin
          ? null
          : this.siteId || null,
      })
    },

    async fetchPredictedFinancialImpact() {
      try {
        if (!this.isShowingCachedSnapshot) this.isLoading = true

        const response = await api.report.getStats(
          this.organizationId,
          REPORT_TYPE,
          this.statsQuery
        )

        this.predictedFinancialImpact =
          response?.data?.stats?.totalPredictedFinancialImpactInUSD ?? 0

        this.saveSnapshot()
        this.isShowingCachedSnapshot = false
      } catch (err) {
        this.handleRequestError(err)

        if (!this.isShowingCachedSnapshot) this.predictedFinancialImpact = 0
      } finally {
        this.isLoading = false

        if (!this.hasData) {
          this.$emit('empty')
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.min-width-0 {
  min-width: 0;
}
</style>
